import {
  useContext,
  type DetailedHTMLProps,
  type InputEvent,
  type InputHTMLAttributes,
  type PropsWithChildren,
} from "react";
import { twMerge } from "tailwind-merge";

import { FormLabelForContext } from "@/contexts/FormLabelForContext";

export function InputContainer({ className, children }: PropsWithChildren<{ className?: string }>) {
  return (
    <div
      data-input-container
      className={twMerge(
        "relative flex min-h-[2.75rem] flex-row items-center rounded-sm border border-divider bg-white has-user-invalid:outline-2 has-user-invalid:outline-danger has-focus:outline-turtle has-focus:not-has-[input.hidden]:outline-2 has-[input.hidden]:border-transparent",
        className,
      )}
    >
      {children}
    </div>
  );
}

export function Input({
  required,
  showRequired = required,
  className,
  onInput,
  name,
  validationName,
  ...props
}: { required?: boolean; showRequired?: boolean; validationName?: string } & DetailedHTMLProps<
  InputHTMLAttributes<HTMLInputElement>,
  HTMLInputElement
>) {
  const id = useContext(FormLabelForContext);

  function handleInput(e: InputEvent<HTMLInputElement>) {
    e.currentTarget.setCustomValidity("");
    onInput?.(e);
  }

  return (
    <div className="flex flex-col not-[[data-input-container]_&]:relative has-[.hidden]:hidden [[data-input-container]_&]:grow [[data-input-container]_&]:basis-0">
      <input
        id={props.id ?? id}
        name={name}
        data-validation-name={validationName}
        onInput={handleInput}
        required={required}
        className={twMerge(
          "h-[2.75rem] rounded-sm border-divider bg-white px-2 not-[[data-input-container]_&]:border not-[[data-input-container]_&]:user-invalid:outline-2 not-[[data-input-container]_&]:user-invalid:outline-danger focus:outline-2 focus:outline-turtle [[data-input-container]_&]:outline-none",
          className,
        )}
        {...props}
      />
      {showRequired && (
        <span className="pointer-events-none absolute top-1 right-2 font-interest text-danger" aria-hidden="true">
          *
        </span>
      )}
    </div>
  );
}
